/**
 * What every booking file needs, whatever tool reads it: which documents can be booked at all,
 * the VAT lines to book, a numeric Beleg number, and text the import cannot choke on.
 */
import type { Category } from "@/lib/api/categories";
import type { DocumentRecord } from "@/lib/api/documents";
import type { AccountingBasis } from "@/lib/at/uva";
import { parseVatLines, type VatLine } from "@/lib/at/vat";
import type { ExportSettings } from "./settings";

export type BookingProblem = {
  documentId: string;
  docNumber: string;
  reason:
    "no_account" | "no_partner" | "no_vat_lines" | "foreign_currency" | "no_vat_id" | "no_tax_key";
};

export type BookingContext = {
  categories: Category[];
  /** Personenkonto per document id (`planPartnerAccounts`). */
  partnerNumbers: Map<string, number>;
  settings: ExportSettings;
  basis: AccountingBasis;
  /** The Sachkonto of the card or bank account a Barbeleg was paid from, when it is known. */
  paidFrom: (document: DocumentRecord) => string | undefined;
  /** Where the document's file lies in the export, as the tool should find it. */
  documentPath: (document: DocumentRecord) => string;
};

/** A document that passed the checks, with what every format books it on. */
export type Bookable = {
  document: DocumentRecord;
  /** Expense or revenue account of its category. */
  account: string;
  /** Personenkonto for ER/AR, cash or card account for a KA. */
  counterAccount: string;
  lines: VatLine[];
  /** Tip on top of the receipt: part of what was paid, no VAT. */
  tip: number;
};

/** The VAT lines to book: the stored breakdown, or the gross as one tax-free line. */
export function bookingLines(document: {
  vatLines?: unknown;
  taxTreatment?: string;
  grossTotal?: number;
}): VatLine[] {
  const lines = parseVatLines(document.vatLines);
  if (lines.length > 0) return lines;
  if ((document.taxTreatment ?? "standard") !== "standard" && (document.grossTotal ?? 0) > 0) {
    return [{ rate: 0, net: document.grossTotal!, vat: 0 }];
  }
  return [];
}

/**
 * Sorts documents into bookable ones and problems. `needsVatId` lets a format insist on the
 * partner's UID where its import rejects the booking without one (RZL: ig. Lieferung).
 */
export function prepareBookings(
  documents: DocumentRecord[],
  context: BookingContext,
  needsVatId: (document: DocumentRecord) => boolean = () => false,
): { bookable: Bookable[]; problems: BookingProblem[] } {
  const categories = new Map(context.categories.map((category) => [category.id, category]));
  const bookable: Bookable[] = [];
  const problems: BookingProblem[] = [];

  for (const document of documents) {
    const problem = (reason: BookingProblem["reason"]) =>
      problems.push({ documentId: document.id, docNumber: document.docNumber ?? "", reason });
    const account = categories.get(document.category)?.accountNumber?.trim();
    const lines = bookingLines(document);
    const partner = context.partnerNumbers.get(document.id);

    if ((document.currency || "EUR") !== "EUR") problem("foreign_currency");
    else if (!account) problem("no_account");
    else if (lines.length === 0) problem("no_vat_lines");
    else if (document.kind !== "KA" && !partner) problem("no_partner");
    else if (needsVatId(document) && !isVatId(document.partnerVatId)) problem("no_vat_id");
    else {
      bookable.push({
        document,
        account,
        counterAccount:
          document.kind === "KA"
            ? context.paidFrom(document) || context.settings.cashAccount
            : String(partner),
        lines,
        tip: document.kind === "AR" ? 0 : (document.tip ?? 0),
      });
    }
  }
  return { bookable, problems };
}

/** A UID in the shape every EU member state uses: country code plus 2-12 letters or digits. */
export function isVatId(value: string | undefined): boolean {
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test((value ?? "").replace(/\s+/g, "").toUpperCase());
}

/**
 * A numeric Beleg/OP number that stays unique across years: ER-2026-0042 -> "20260042". BMD and
 * RZL both want digits there, and a plain 42 would meet next year's 42 on the same Personenkonto.
 */
export function numericBelegnr(document: Pick<DocumentRecord, "docNumber" | "seq">): string {
  const match = /(\d{4})-(\d+)$/.exec(document.docNumber ?? "");
  if (match) return `${match[1]}${match[2]!.padStart(4, "0")}`;
  return String(document.seq ?? 0);
}

/** "2026-03-14 00:00:00.000Z" -> ["14", "03", "2026"]. */
export function dateParts(value: string | undefined): [string, string, string] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return match ? [match[3]!, match[2]!, match[1]!] : null;
}

/** Text without the field separator or line breaks, cut to what the tool keeps. */
export function clean(value: string, max: number): string {
  return value
    .replace(/[;\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

export const decimal = (value: number) => String(value).replace(".", ",");
